/**
 * Topics API client — Workspace intelligence organization
 *
 * CRUD for topics (named contexts grouping conversations, issues, documents).
 */

import type { WorkpieceType, Stage, CardPayload, CardState, Reaction, WorkpieceOpResult } from './topicsWorkpiece';
import type { EmailThread } from './comms';
export type { WorkpieceType, Stage, CardPayload, CardState, Reaction, WorkpieceOpResult } from './topicsWorkpiece';

const BASE = '/api/orchestration/topics';

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {};
  // Only set Content-Type when there's a body — Fastify rejects empty JSON bodies
  if (init?.body) headers['Content-Type'] = 'application/json';
  const res = await fetch(path, {
    headers,
    credentials: 'include',
    ...init,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return res.json();
}

// ─── Types ─────────────────────────────────────────────────────────────────────

/** A thinking-state step event streamed from the agent (spec §6). */
export interface ThinkingStateEvent {
  type: 'thinking_state';
  /** Monotonic within the task; UI uses to order the list. 1-indexed. */
  display_order: number;
  /** Stable id derived from display_order — `step-<n>` — unique per task. */
  stepId: string;
  phase: 'upload' | 'detect' | 'store' | 'agent';
  status: 'pending' | 'active' | 'complete' | 'error';
  message: string;
  emitted_at: string;
  error_message?: string;
}

export interface Topic {
  id: string;
  type: string; // legacy compatibility field; do not use for routing or UI taxonomy
  title: string;
  goal: string | null;
  goal_set_at: string | null;
  description: string | null;
  notes: string | null;
  instructions: string | null;
  status: 'active' | 'completed' | 'archived';
  color_index: number;
  workspace_id: string;
  object_type: string | null;
  object_id: string | null;
  /** Primary deliverable type. Drives the WorkpieceSlot on the right pane. */
  workpiece_type: WorkpieceType;
  /** ID of the workpiece (email thread_id, document doc_id, issue list/board id). */
  workpiece_id: string | null;
  /** Active stage of the topic flow (advisory chrome; not enforced). */
  stage: Stage;
  assigned_agents: string[];
  participant_user_ids: string[];
  is_background: boolean;
  needs_attention: boolean;
  context: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  archived_at: string | null;
  last_agent_activity_at: string | null;
  items: TopicItem[];
}

export interface TopicItem {
  id: number;
  topic_id: string;
  item_type: string;
  item_id: string;
  label: string | null;
  added_at: string;
  added_by: string | null;
  project_id?: string | null;
  is_template?: boolean;
}

export interface TopicEmailWorkpiece {
  topic_id: string;
  workpiece_type: 'email';
  workpiece_id: string;
  connection: {
    id: string;
    email_address: string | null;
  };
  thread: EmailThread;
}

export interface TopicExecution {
  workflowId: string;
  label?: string;
  startedAt: string;
  status: string;
  startTime?: string;
  closeTime?: string;
  prUrl?: string;
  signals: Array<Record<string, unknown>>;
}

export interface TopicFile {
  id: string;
  filename: string;
  mime_type: string;
  file_size: string;
  status: string;
  created_at: string;
  download_url: string | null;
  extracted_text?: string;
}

export interface PendingAction {
  id: string;
  topic_id: string;
  agent_id: string;
  agent_name: string;
  action_type: string;
  summary: string;
  payload: Record<string, unknown>;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  reviewed_by: string | null;
  review_note: string | null;
  created_at: string;
  reviewed_at: string | null;
  expires_at: string;
}

export interface TopicTemplate {
  id: string;
  name: string;
  description: string | null;
  type: string;
  default_agents: Array<{ agent_id: string; role: string }>;
  default_goal: string | null;
  default_instructions: string | null;
  schedule: { cron: string; timezone?: string } | null;
}

export interface TopicPlanStep {
  index: number;
  title: string;
  detail: string;
  status: 'pending' | 'in_progress' | 'completed' | 'blocked';
}

export interface TopicPlanState {
  id: string;
  title: string;
  goal: string | null;
  status: 'draft' | 'active' | 'executing' | 'completed' | 'abandoned' | 'superseded';
  steps: TopicPlanStep[];
  decisions: unknown[];
  assumptions: unknown[];
  updated_at: string;
}

export interface TopicColor {
  name: string;
  bg: string;
  text: string;
  light: string;
  ring: string;
}

// Topic color palette (6 colors, cycled by color_index)
export const TOPIC_COLORS: readonly TopicColor[] = [
  { name: 'coral',    bg: 'bg-coral-500',    text: 'text-white', light: 'bg-coral-50',    ring: 'ring-coral-300' },
  { name: 'blue',     bg: 'bg-blue-500',     text: 'text-white', light: 'bg-blue-50',     ring: 'ring-blue-300' },
  { name: 'emerald',  bg: 'bg-emerald-500',  text: 'text-white', light: 'bg-emerald-50',  ring: 'ring-emerald-300' },
  { name: 'violet',   bg: 'bg-violet-500',   text: 'text-white', light: 'bg-violet-50',   ring: 'ring-violet-300' },
  { name: 'amber',    bg: 'bg-amber-500',    text: 'text-white', light: 'bg-amber-50',    ring: 'ring-amber-300' },
  { name: 'rose',     bg: 'bg-rose-500',     text: 'text-white', light: 'bg-rose-50',     ring: 'ring-rose-300' },
];

export function getTopicColor(colorIndex: number): TopicColor {
  return TOPIC_COLORS[colorIndex % TOPIC_COLORS.length];
}

// ─── Page context ───────────────────────────────────────────────────────────────

export interface PageContext {
  url: string;
  project_id?: string;
  document_id?: string;
  page_code?: string;
  pane?: string;
  /** Active canvas tab when inside a topic workspace (e.g. 'goal001', 'docs001', 'mail001') */
  active_tab?: string;
  /** Design page context — set when user is viewing a design system gallery */
  design_context?: { brand: string; platform: string };
}

// Map new semantic page segment names → 2-letter legacy page codes
const PAGE_SEGMENT_TO_CODE: Record<string, string> = {
  documents: 'do', boards: 'bo', epics: 'ep', sprints: 'sp',
  monitor: 'mo', editor: 'me', testing: 'te', coder: 'co',
  staging: 'st', automations: 'au', environment: 'en', security: 'se',
  worktrees: 'wt', queue: 'qu', templates: 'tl', trash: 'tr',
};

/** Derive page context from the current URL for document/board/sprint/issue scoping. */
export function derivePageContext(): PageContext | undefined {
  const pathname = window.location.pathname;
  if (!pathname || pathname === '/') return undefined;

  const ctx: PageContext = { url: pathname };

  // New semantic routes: /w/:ws/product/:projectId/...
  const newProjectMatch = pathname.match(/\/w\/[^/]+\/product\/([^/]+)/);
  if (newProjectMatch) {
    ctx.project_id = newProjectMatch[1];
    ctx.pane = 'product';
    // Extract page segment after project_id: /product/:id/documents/:docId
    const pageSegMatch = pathname.match(/\/product\/[^/]+\/([^/]+)(?:\/([^/]+))?/);
    if (pageSegMatch) {
      const seg = pageSegMatch[1];
      ctx.page_code = PAGE_SEGMENT_TO_CODE[seg] || seg;
      if ((seg === 'documents' || seg === 'editor') && pageSegMatch[2]) {
        ctx.document_id = pageSegMatch[2];
      }
    }
  }

  // Legacy routes: /w/:ws/pr/:projectId/...
  if (!ctx.project_id) {
    const projectMatch = pathname.match(/\/pr\/([^/]+)/);
    if (projectMatch) {
      ctx.project_id = projectMatch[1];
      const codeMatch = pathname.match(/\/pr\/[^/]+\/([a-z]{2})/);
      if (codeMatch) ctx.page_code = codeMatch[1];
    }
  }

  // Legacy: /do/:docId or /me/:docId
  if (!ctx.document_id) {
    const docMatch = pathname.match(/\/do\/([^/]+)/);
    if (docMatch) ctx.document_id = docMatch[1];
    const meMatch = pathname.match(/\/me\/([^/]+)/);
    if (meMatch) ctx.document_id = meMatch[1];
  }

  // Extract pane from legacy 2-letter codes: /w/:ws/:pane (pr, su, sm, ar)
  if (!ctx.pane) {
    const paneMatch = pathname.match(/\/w\/[^/]+\/([a-z]{2,})/);
    if (paneMatch) ctx.pane = paneMatch[1];
  }

  // Parse query params once
  const searchParams = new URLSearchParams(window.location.search);

  // If no document_id from path, check ?doc= query param (topic deep link)
  if (!ctx.document_id) {
    const docParam = searchParams.get('doc');
    if (docParam) ctx.document_id = docParam;
  }

  // When inside a topic, include the active canvas tab.
  // If we're on a document page, the active tab is docs001 regardless of ?tab=
  if (searchParams.get('topic')) {
    const tabParam = searchParams.get('tab');
    const docParam = searchParams.get('doc');
    const isDocPage = ctx.page_code === 'do' || ctx.page_code === 'me';
    ctx.active_tab = (docParam || isDocPage)
      ? 'docs001'
      : (tabParam || 'goal001');
  }

  // Design pages — Archie Design Studio (/ar/dg or /archie/design-studio) or Martha Settings → Design
  if (ctx.pane === 'ar' && ctx.page_code === 'dg') {
    ctx.design_context = { brand: 'archie', platform: 'web' };
  } else if (pathname.includes('/settings') && searchParams.get('category') === 'design') {
    ctx.design_context = { brand: 'martha', platform: 'web' };
  }

  return Object.keys(ctx).length > 1 ? ctx : undefined;
}

// ─── API ───────────────────────────────────────────────────────────────────────

export const topicsApi = {
  list: (opts: { workspace_id: string; status?: string; limit?: number }) => {
    const params = new URLSearchParams();
    params.set('workspace_id', opts.workspace_id);
    if (opts.status) params.set('status', opts.status);
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return fetchJson<{ topics: Topic[] }>(`${BASE}${qs ? `?${qs}` : ''}`);
  },

  get: (id: string) =>
    fetchJson<{ topic: Topic }>(`${BASE}/${id}`),

  getEmailWorkpiece: (id: string) =>
    fetchJson<TopicEmailWorkpiece>(`${BASE}/${id}/workpiece/email`),

  create: (input: {
    type?: string;
    title: string;
    description?: string;
    workspace_id: string;
    object_type?: string;
    object_id?: string;
    workpiece_type?: WorkpieceType;
    workpiece_id?: string | null;
    assigned_agents?: string[];
    is_background?: boolean;
    context?: Record<string, unknown>;
  }) =>
    fetchJson<{ topic: Topic }>(BASE, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  update: (id: string, input: Partial<Pick<Topic, 'title' | 'goal' | 'notes' | 'instructions' | 'status' | 'is_background' | 'needs_attention' | 'assigned_agents' | 'context'>>) =>
    fetchJson<{ topic: Topic }>(`${BASE}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  addItem: (topicId: string, input: {
    item_type: string;
    item_id: string;
    label?: string;
  }) =>
    fetchJson<{ item: TopicItem }>(`${BASE}/${topicId}/items`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  removeItem: (topicId: string, itemId: number) =>
    fetchJson<{ removed: boolean }>(`${BASE}/${topicId}/items/${itemId}`, {
      method: 'DELETE',
    }),

  promoteChat: (input: {
    talk_session_id: string;
    workspace_id: string;
    title?: string;
  }) =>
    fetchJson<{ topic: Topic }>(`${BASE}/promote-chat`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  setBackground: (id: string, is_background: boolean) =>
    fetchJson<{ topic: Topic }>(`${BASE}/${id}/background`, {
      method: 'POST',
      body: JSON.stringify({ is_background }),
    }),

  getActivePlan: (topicId: string, conversationId?: string) => {
    const params = new URLSearchParams();
    if (conversationId) params.set('conversation_id', conversationId);
    const qs = params.toString();
    return fetchJson<{ topic_id: string; conversation_id: string; plan: TopicPlanState | null }>(
      `${BASE}/${topicId}/plan${qs ? `?${qs}` : ''}`,
    );
  },

  executePlan: (topicId: string, conversationId?: string) =>
    fetchJson<{ topic_id: string; conversation_id: string; plan: TopicPlanState }>(`${BASE}/${topicId}/plan/execute`, {
      method: 'POST',
      body: JSON.stringify({ conversation_id: conversationId }),
    }),

  updatePlanStatus: (topicId: string, status: 'active' | 'executing' | 'completed' | 'abandoned', conversationId?: string) =>
    fetchJson<{ topic_id: string; conversation_id: string; plan: TopicPlanState }>(`${BASE}/${topicId}/plan/status`, {
      method: 'PATCH',
      body: JSON.stringify({ conversation_id: conversationId, status }),
    }),

  updatePlanStepStatus: (
    topicId: string,
    stepIndex: number,
    status: TopicPlanStep['status'],
    conversationId?: string,
  ) =>
    fetchJson<{ topic_id: string; conversation_id: string; plan: TopicPlanState }>(`${BASE}/${topicId}/plan/steps/${stepIndex}`, {
      method: 'PATCH',
      body: JSON.stringify({ conversation_id: conversationId, status }),
    }),

  /** List uploaded files for a topic. */
  getTopicFiles: (topicId: string): Promise<{ files: TopicFile[] }> =>
    fetchJson<{ files: TopicFile[] }>(`/api/files/by-topic/${topicId}`),

  /** Delete an uploaded file. */
  deleteFile: (fileId: string): Promise<void> =>
    fetchJson<void>(`/api/files/${fileId}`, { method: 'DELETE' }),

  /** Upload a file attachment for a topic. Returns the file metadata from the server. */
  uploadAttachment: async (
    topicId: string | null,
    file: File,
    workspaceId: string,
    onProgress?: (pct: number) => void,
  ): Promise<{ id: string; filename: string; mime_type: string; file_size: number; status: string; download_url: string | null }> => {
    const formData = new FormData();
    // Fields MUST be appended before the file — @fastify/multipart parses
    // the stream sequentially and fields after the file part are not available.
    formData.append('workspace_id', workspaceId);
    if (topicId) formData.append('topic_id', topicId);
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    const result = await new Promise<{ id: string; filename: string; mime_type: string; file_size: number; status: string; download_url: string | null }>((resolve, reject) => {
      xhr.open('POST', '/api/files/upload');
      xhr.withCredentials = true;

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          try {
            const err = JSON.parse(xhr.responseText);
            reject(new Error(err.error || `Upload failed: ${xhr.status}`));
          } catch {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Upload failed — network error')));
      xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

      xhr.send(formData);
    });

    return result;
  },

  /** Stream chat via SSE — emits status, token, thinking, done, error events.
   *
   *  Retry policy: up to 4 attempts with exponential backoff (1s, 2s, 4s, 8s)
   *  on 502/503/504 or transient network errors that happen BEFORE the SSE
   *  stream starts. Total worst-case wait ≈ 15s — enough to ride out an
   *  orchestration-service container restart (typically 20-45s). If all
   *  attempts fail, onError() fires with a human-readable message so the
   *  caller can surface a "Send failed — retry" banner. */
  chatStream: (
    topicId: string,
    message: string,
    userInfo: { user_name?: string; user_email?: string; user_id?: string } | undefined,
    callbacks: {
      onStatus?: (text: string, phase: string) => void;
      onToken?: (text: string) => void;
      onThinking?: (text: string) => void;
      /** A discrete thinking-state step (parsing, structuring, generating…).
       *  Each event carries a unique monotonic display_order/stepId for this
       *  task, so multi-turn tool phases never collapse into one entry. */
      onThinkingState?: (event: ThinkingStateEvent) => void;
      onArtifact?: (artifact: { tool_name: string; label: string; icon: string | null; data: unknown; stored_at: string; room_id?: string }) => void;
      onArtifactRoomInit?: (init: { tool_use_id: string; tool_name: string; room_id: string }) => void;
      onDone?: (data: { conversation_id: string; assistant_message: Record<string, unknown>; isPlan?: boolean }) => void;
      onError?: (error: string) => void;
      /** #32 — the turn was cut short on the wire (user Stop / stream died).
       *  The supporting change emits `event: response.interrupted` carrying
       *  {message_id, conversation_id, query_id, reason, partial, recoverable};
       *  forwarded here so the consumer can settle the stream + render the cue. */
      onInterrupted?: (data: { message_id?: string | null; conversation_id?: string | null; query_id?: string | null; reason: string; partial: boolean; recoverable: boolean }) => void;
      onTurnDone?: (data: { turn: number; is_final: boolean; assistant_message: Record<string, unknown> }) => void;
      /** Fired when an agent emits a verb-typed card (Phase 2 — workpiece+flow). */
      onCard?: (data: {
        message_id: number;
        conversation_id: string;
        verb: 'ask' | 'suggest' | 'decide' | 'act';
        card_payload: CardPayload;
        created_at: string;
        agent_id: string;
        agent_name: string;
        turn?: number;
      }) => void;
    },
    attachmentIds?: string[],
    pageContext?: PageContext,
    options?: { agentMode?: boolean; agentLoop?: boolean; researchContext?: string },
  ): AbortController => {
    const controller = new AbortController();
    const payload: Record<string, unknown> = { message, ...userInfo };
    if (attachmentIds?.length) payload.attachment_ids = attachmentIds;
    if (pageContext) payload.page_context = pageContext;
    if (options?.agentMode) payload.agent_mode = true;
    if (options?.agentLoop) payload.agent_loop = true;
    if (options?.researchContext) payload.research_context = options.researchContext;
    const body = JSON.stringify(payload);

    const MAX_ATTEMPTS = 4;
    // Exponential backoff before attempt N (0-indexed): 0s, 1s, 2s, 4s
    // Total worst-case wait ≈ 7s between attempts, + request overhead → ~15s.
    const backoffMs = (attemptNumber: number) => attemptNumber === 0 ? 0 : Math.min(8000, 1000 * 2 ** (attemptNumber - 1));

    const attempt = async (attemptNumber: number): Promise<void> => {
      if (attemptNumber > 0) {
        await new Promise(r => setTimeout(r, backoffMs(attemptNumber)));
        // After a backoff the caller may have aborted; check and bail
        if (controller.signal.aborted) return;
      }
      try {
        const res = await fetch(`${BASE}/${topicId}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body,
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          const err = await res.json().catch(() => ({ error: res.statusText }));
          // Retry on transient gateway errors (502/503/504) — orchestration
          // container restarts typically return 502 via tunnel-proxy for 20-45s.
          if (attemptNumber + 1 < MAX_ATTEMPTS && res.status >= 502 && res.status <= 504) {
            return attempt(attemptNumber + 1);
          }
          // Surface the backend's classified error, or provide a helpful fallback
          let errorMsg = err.error ?? 'Request failed';
          if (res.status === 401) errorMsg = 'Authentication failed — please refresh the page.';
          else if (res.status === 502) errorMsg = 'Martha is restarting — message not sent. Click retry when ready.';
          else if (res.status === 503) errorMsg = 'Martha is temporarily unavailable — click retry to try again.';
          else if (res.status === 504) errorMsg = 'Martha timed out — click retry to try again.';
          callbacks.onError?.(errorMsg);
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let receivedDone = false;
        let receivedError = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? ''; // Keep incomplete line in buffer

          let currentEvent = '';
          let currentData = '';
          for (const line of lines) {
            if (controller.signal.aborted) break;
            // Skip heartbeat comments
            if (line.startsWith(':')) continue;
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              currentData = line.slice(6);
            } else if (line === '' && currentEvent && currentData) {
              try {
                const parsed = JSON.parse(currentData);
                if (currentEvent === 'status') callbacks.onStatus?.(parsed.text, parsed.phase);
                else if (currentEvent === 'token') callbacks.onToken?.(parsed.text);
                else if (currentEvent === 'thinking') callbacks.onThinking?.(parsed.text);
                else if (currentEvent === 'thinking_state') callbacks.onThinkingState?.(parsed);
                else if (currentEvent === 'artifact') callbacks.onArtifact?.(parsed);
                else if (currentEvent === 'artifact_room_init') callbacks.onArtifactRoomInit?.(parsed);
                else if (currentEvent === 'turn_done') { callbacks.onTurnDone?.(parsed); }
                else if (currentEvent === 'card') { callbacks.onCard?.(parsed); }
                else if (currentEvent === 'done') { receivedDone = true; callbacks.onDone?.(parsed); }
                else if (currentEvent === 'error') { receivedError = true; callbacks.onError?.(parsed.error); }
                else if (currentEvent === 'response.interrupted') {
                  // Mark settled so the trailing close treats this as a normal EOF.
                  receivedDone = true;
                  callbacks.onInterrupted?.({
                    message_id: parsed.message_id ?? parsed.messageId ?? null,
                    conversation_id: parsed.conversation_id ?? parsed.conversationId ?? null,
                    query_id: parsed.query_id ?? parsed.queryId ?? null,
                    reason: typeof parsed.reason === 'string' ? parsed.reason : 'interrupted',
                    partial: parsed.partial === true,
                    recoverable: parsed.recoverable === true,
                  });
                }
              } catch { /* skip malformed */ }
              currentEvent = '';
              currentData = '';
            }
          }
        }

        // Stream ended — ensure a terminal callback fired
        if (!receivedDone && !receivedError) {
          callbacks.onError?.('Martha\'s response was interrupted — try sending your message again.');
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;
        // Retry on transient network errors (fetch throws during service restart)
        if (attemptNumber + 1 < MAX_ATTEMPTS) {
          return attempt(attemptNumber + 1);
        }
        // Classify the error for a human-readable message
        const raw = err instanceof Error ? err.message : String(err);
        let userMsg = raw || 'Stream failed';
        if (raw.includes('Failed to fetch') || raw.includes('NetworkError') || raw.includes('fetch')) {
          userMsg = 'Could not reach Martha — service may be restarting. Click retry to try again.';
        } else if (raw.includes('401') || raw.includes('authentication') || raw.includes('api-key')) {
          userMsg = 'AI service authentication failed — API key may be invalid or expired.';
        }
        callbacks.onError?.(userMsg);
      }
    };

    attempt(0);
    return controller;
  },

  join: (topicId: string, agentInfo: { agent_id: string; agent_name: string }) =>
    fetchJson<{ topic: Topic; conversation_id: string; joined_as: string }>(`${BASE}/${topicId}/join`, {
      method: 'POST',
      body: JSON.stringify(agentInfo),
    }),

  getExecution: (topicId: string) =>
    fetchJson<{ executions: TopicExecution[] }>(`${BASE}/${topicId}/execution`),

  triggerExecution: (topicId: string, task: string, opts?: { baseBranch?: string; targetBranch?: string }) =>
    fetchJson<{ workflowId: string; branch: string }>(`${BASE}/${topicId}/execute`, {
      method: 'POST',
      body: JSON.stringify({ task, ...opts }),
    }),

  // ── Approval gates ────────────────────────────────────────────────────────

  getPendingActions: (topicId: string) =>
    fetchJson<{ actions: PendingAction[] }>(`${BASE}/${topicId}/pending-actions`),

  approveAction: (topicId: string, actionId: string, note?: string) =>
    fetchJson<{ ok: boolean; action: PendingAction }>(`${BASE}/${topicId}/approve/${actionId}`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  rejectAction: (topicId: string, actionId: string, note?: string) =>
    fetchJson<{ ok: boolean; action: PendingAction }>(`${BASE}/${topicId}/reject/${actionId}`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  // ── Verb-typed card reactions (Phase 2 — workpiece+flow rethink) ───────────

  cards: {
    /**
     * React to a verb-typed card emitted by an agent. Persists card_state,
     * inserts a synthesized human_intervention message describing the
     * reaction, and emits an SSE 'card_reacted' event so all open clients
     * see the resolved state immediately.
     */
    react: (
      topicId: string,
      cardId: number,
      body: { reaction: Reaction; edit_content?: string; choice_id?: string },
    ) =>
      fetchJson<{ ok: boolean; message_id: number; card_state: CardState; synth_line?: string; workpiece_op_result?: WorkpieceOpResult | null }>(
        `${BASE}/${topicId}/cards/${cardId}/react`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
  },

  // ── Collaboration ─────────────────────────────────────────────────────────

  getTemplates: (workspaceId?: string) =>
    fetchJson<{ templates: TopicTemplate[] }>(`${BASE.replace('/topics', '')}/topic-templates${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  handoff: (topicId: string, data: { from_agent_id: string; to_agent_id: string; message: string; context?: Record<string, unknown> }) =>
    fetchJson<{ ok: boolean; message_id: string; conversation_id: string }>(`${BASE}/${topicId}/handoff`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};
